import { useEffect, useMemo, useState } from 'react';
import getDoctorPublicProfile from '../api/getDoctorPublicProfile';
import getCity from '../../../api/getCity';
import getSpeciality from '../../../api/getSpecialties';
import getSecondarySpecialties from '../api/getSecondarySpecialties';
import getSecondarySpecialtiesByPrimaryId from '../api/getSecondarySpecialtiesByPrimaryId';
import getLanguagesInProfile from '../api/getLanguagesInProfile';
import getLanguages from '../api/getLanguages';
import getAllExperiences from '../api/getAllExperiences';
import getAllEducations from '../api/getAllEducations';
import getAllMotives from '../api/getAllMotives';
import getAllSchedules from '../api/getAllSchedules';
import getAllAvailabilities from '../api/getAllAvailabilities';
import getProfileApprovalRecord from '../api/getProfileApprovalRecord';
import getAllServices from '../api/getAllServices';

export default function useDoctorUserProfile(selectedUser) {
    const [isLoading, setIsLoading] = useState(true);
    const [basicProfile, setBasicProfile] = useState(null);
    const [cities, setCities] = useState(null);
    const [specialities, setSpecialities] = useState(null);
    const [secondarySpecialties, setSecondarySpecialties] = useState(null);
    const [availableSecondarySpecialties, setAvailableSecondarySpecialties] = useState(null);
    const [languages, setLanguages] = useState(null);
    const [experiences, setExperiences] = useState(null);
    const [education, setEducation] = useState(null);
    const [motives, setMotives] = useState(null);
    const [services, setServices] = useState(null);
    const [schedules, setSchedules] = useState(null);
    const [availabilities, setAvailabilities] = useState(null);
    const [languagesInProfile, setLanguagesInProfile] = useState(null);
    const [profileApproval, setProfileApproval] = useState(null);
    const [errorMessage, setErrorMessage] = useState(false);

    const queryBasicProfile = async () => {
        
        const getBasicProfileResult = await getDoctorPublicProfile(selectedUser.id, setErrorMessage);

        if (getBasicProfileResult && !getBasicProfileResult.hasProfile) {
            setBasicProfile({});
            return false;
        }

        if (getBasicProfileResult) {
            setBasicProfile(getBasicProfileResult);
            return true;
        }

        setErrorMessage('No se pudo completar la solicitud. Inténtelo de nuevo.');
    };

    const queryCities = async () => {
        const getCities = await getCity(setErrorMessage);

        if (getCities) {
            setCities(getCities);
            return;
        }

        setErrorMessage('No se pudo completar la solicitud. Inténtelo de nuevo.');
    };

    const querySpecialities = async () => {
        const getSpecialities = await getSpeciality(setErrorMessage);

        if (getSpecialities) {
            setSpecialities(getSpecialities);
            return;
        }

        setErrorMessage('No se pudo completar la solicitud. Inténtelo de nuevo.');
    };

    const queryLanguages = async () => {
        const getAvailableLanguages = await getLanguages(setErrorMessage);

        if (getAvailableLanguages) {
            setLanguages(getAvailableLanguages);
            return;
        }

        setErrorMessage('No se pudo completar la solicitud de lenguajes. Inténtelo de nuevo.');
    };

    const queryProfileApproval = async () => {
        const getProfileApproval = await getProfileApprovalRecord(selectedUser.id);

        if (getProfileApproval) {
            setProfileApproval(getProfileApproval);
        }
    };

    const queryAllDoctorExperiences = async () => {
        const getAvailableExperiences = await getAllExperiences(selectedUser.id, setErrorMessage);

        if (getAvailableExperiences) {
            setExperiences(getAvailableExperiences);
            return;
        }

        setErrorMessage('No se pudo completar la solicitud de experiencias. Inténtelo de nuevo.');
    };

    const queryAllDoctorEducation = async () => {
        const getAvailableEducation = await getAllEducations(selectedUser.id, setErrorMessage);

        if (getAvailableEducation) {
            setEducation(getAvailableEducation);
            return;
        }

        setErrorMessage('No se pudo completar la solicitud de educación. Inténtelo de nuevo.');
    };

    const queryAllDoctorMotives = async () => {
        const getAvailableMotives = await getAllMotives(selectedUser.id, setErrorMessage);

        if (getAvailableMotives) {
            setMotives(getAvailableMotives);
            return;
        }

        setErrorMessage('No se pudo completar la solicitud de educación. Inténtelo de nuevo.');
    };

    const queryAllDoctorServices = async () => {
        const getAvailableServices = await getAllServices(selectedUser.id, setErrorMessage);

        if (getAvailableServices) {
            setServices(getAvailableServices);
            return;
        }

        setErrorMessage('No se pudo completar la solicitud de servicios. Inténtelo de nuevo.');
    }

    const queryAllDoctorSchedules = async () => {
        const getAvailableSchedules = await getAllSchedules(selectedUser.id, setErrorMessage);
        
        if (getAvailableSchedules) {
            setSchedules(getAvailableSchedules);
            return;
        }

        setErrorMessage('No se pudo completar la solicitud de horarios. Inténtelo de nuevo.');
    };

    const queryAllDoctorAvailabilities = async () => {
        const getAvailableAvailabilities = await getAllAvailabilities(selectedUser.id, setErrorMessage);

        if (getAvailableAvailabilities) {
            setAvailabilities(getAvailableAvailabilities);
            return;
        }

        setErrorMessage('No se pudo completar la solicitud de horarios. Inténtelo de nuevo.');
    };

    const querySecondarySpecialities = async () => {
        const getSecondarySpecialities = await getSecondarySpecialties(selectedUser.id, setErrorMessage);
        if (Array.isArray(getSecondarySpecialities)) {
            const formattedSecondary = getSecondarySpecialities.map((secondary) => ({
                id: secondary.idSpecialtyDetail,
                label: secondary.name,
                key: secondary.idSpecialtyDetail
            }));
            setSecondarySpecialties(formattedSecondary);
            return;
        }

        setErrorMessage('No se pudo completar la solicitud de especialidades secundarias. Inténtelo de nuevo.');
    };

    const querySecondarySpecialitiesByPrimaryId = async (primarySpecialtyId) => {
        if (!primarySpecialtyId) {
            setAvailableSecondarySpecialties(null);
            return;
        }

        const getSecondarySpecialities = await getSecondarySpecialtiesByPrimaryId(primarySpecialtyId, setErrorMessage);
        if (Array.isArray(getSecondarySpecialities)) {
            const formattedSecondary = getSecondarySpecialities.map((secondary) => {
                const id = secondary.id ?? secondary.idSpecialtyDetail;
                return {
                    id,
                    label: secondary.name,
                    key: id
                };
            });
            setAvailableSecondarySpecialties(formattedSecondary);
            return;
        }

        setErrorMessage('No se pudo completar la solicitud de especialidades secundarias. Inténtelo de nuevo.');
    };

    const queryLanguagesInProfile = async () => {
        const getLanguages = await getLanguagesInProfile(selectedUser.id, setErrorMessage);

        if (getLanguages) {
            const formattedLanguages = getLanguages.map((language) => ({
                id: language.idLanguageByDoctor,
                label: language.name,
                key: language.idLanguageByDoctor
            }));
            setLanguagesInProfile(formattedLanguages);
            return;
        }

        setErrorMessage('No se pudo completar la solicitud de especialidades secundarias. Inténtelo de nuevo.');
    };

    const resetState = () => {
        setBasicProfile(null);
        setCities(null);
        setSecondarySpecialties(null);
        setAvailableSecondarySpecialties(null);
        setExperiences(null);
        setAvailabilities(null);
        setLanguagesInProfile(null);
        setErrorMessage(null);
    };

    const formattedSecondarySpecialties = useMemo(() => {
        if (!availableSecondarySpecialties) return;

        const alreadyInProfile = secondarySpecialties ?? [];

        return availableSecondarySpecialties
            .filter((spe) => {
                return alreadyInProfile.findIndex((second) => second.label === spe.label) === -1;
            })
            .map((spe) => {
                return {
                    id: spe.id,
                    label: spe.label
                };
            });
    }, [availableSecondarySpecialties, secondarySpecialties]);

    const formattedLanguages = useMemo(() => {
        if (!basicProfile || !languages || !languagesInProfile) return;

        return languages
            .filter((lang) => {
                return languagesInProfile.findIndex((inProfile) => inProfile.label === lang.name) === -1;
            })
            .map((lang) => {
                return {
                    id: lang.id,
                    label: lang.name
                };
            });
    }, [basicProfile, languages, languagesInProfile]);

    const schedulesAsOptions = useMemo(() => {
        return (
            schedules &&
            schedules
                .filter((item) => item.status)
                .map((item) => ({
                    id: item.id,
                    label: `${item.dayName}: ${item.openHours1} - ${item.openHours2}`
                }))
        );
    }, [schedules]);

    const fetchData = async () => {
        resetState();
        const getBasicProfile = await queryBasicProfile();
        
        queryCities();
        querySpecialities();

        if (getBasicProfile) {
            console.log('profile apis called');
            querySecondarySpecialities();
            queryLanguages();
            queryLanguagesInProfile();
            queryAllDoctorExperiences();
            queryAllDoctorEducation();
            queryAllDoctorMotives();
            queryAllDoctorServices();
            queryAllDoctorSchedules();
            queryAllDoctorAvailabilities();
            queryProfileApproval();
        }

        setIsLoading(false);
    };

    useEffect(() => {

        fetchData();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        basicProfile,
        errorMessage,
        setErrorMessage,
        cities,
        isLoading,
        specialities,
        queryBasicProfile,
        secondarySpecialties,
        formattedSecondarySpecialties,
        querySecondarySpecialities,
        querySecondarySpecialitiesByPrimaryId,
        availableSecondarySpecialties,
        queryLanguagesInProfile,
        languages,
        languagesInProfile,
        formattedLanguages,
        queryAllDoctorExperiences,
        experiences,
        queryAllDoctorEducation,
        education,
        motives,
        queryAllDoctorMotives,
        services,
        queryAllDoctorServices,
        schedules,
        queryAllDoctorSchedules,
        availabilities,
        queryAllDoctorAvailabilities,
        schedulesAsOptions,
        queryProfileApproval,
        profileApproval,
        fetchData
    };
}
